import { css } from '@emotion/react';
import { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SectionHeading from '../SectionHeading';
import Takeover from '../../Takeover';
import Button from '../../Button';
import { spacing, fontStyles, colors } from '../../../styles';
import externalLink from '../../../assets/externalLink.svg';
import chatIcon from '../../Portfolios/icons/chat.svg';

interface Props {
  name: string;
  category: string;
  description: string;
  website?: string;
  investorRelationsUrl?: string;
  industry?: string;
  sector?: string;
  ceo?: string;
  employees?: number;
  headquarters?: string;
  isEtfOrMutualFund?: boolean;
}

const styles = {
  attributesContainer: css`
    display: block;
    margin: -${spacing.deci} 0 ${spacing.centi} 0;
  `,
  attributeText: css`
    ${fontStyles.flea}
    color: ${colors.tungsten};
    text-transform: capitalize;
    html[data-theme='dark'] & {
      color: ${colors.sugar};
    }
  `,
  table: css`
    border-collapse: collapse;
    width: 100%;
  `,
  cell: css`
    width: 50%;
    vertical-align: text-top;
    padding: 0 0 ${spacing.centi} 0;
    html[data-theme='dark'] & {
      color: ${colors.sugar};
    }
  `,
  label: css`
    text-align: left;
    color: ${colors.tungsten};
    ${fontStyles.cat}
  `,
  value: css`
    text-align: right;
    ${fontStyles.roboCat}
    html[data-theme='dark'] & {
      color: ${colors.sugar};
    }
  `,
  shortenedDescription: css`
    ${fontStyles.mouse}
    font-weight: 400;
    margin: 0 0 ${spacing.regular} 0;
    html[data-theme='dark'] & {
      color: ${colors.sugar};
    }
  `,
  externalLink: css`
    display: block;
    margin-bottom: ${spacing.centi};
    ${fontStyles.roboMouse}
    html[data-theme='dark'] & {
      color: ${colors.skyBlue};
    }
  `,
  externalLinkIcon: css`
    vertical-align: text-bottom;
  `,
  sectionHeadingStyles: css`
    ${fontStyles.horse};
    margin: 0 0 ${spacing.centi} 0;
    html[data-theme='dark'] & {
      color: ${colors.sugar};
    }
  `,
  helperTextStyles: css`
    ${fontStyles.mouse};
    color: ${colors.silver};
    margin: 0 0 ${spacing.centi} 0;
    font-weight: 400;
    html[data-theme='dark'] & {
      color: ${colors.tungsten};
    }
  `,
  chatButtonsRowStyles: css`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: ${spacing.centi};
    margin-top: ${spacing.centi};
    margin-bottom: ${spacing.deci};
  `,
  chatButtonStyles: css`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${spacing.centi} ${spacing.milli};
    background-color: ${colors.snow};
    color: ${colors.petrolBlue};
    border: 0.5px solid ${colors.cloud};
    border-radius: 8px;
    ${fontStyles.flea};
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    gap: ${spacing.micro};
    white-space: nowrap;

    &:hover {
      background-color: ${colors.petrolBlue}10;
    }

    html[data-theme='dark'] & {
      background-color: transparent;
      color: ${colors.skyBlue};
      border-color: ${colors.cloud};
      &:hover {
        background-color: ${colors.skyBlue}10;
      }
    }
  `,
  chatIconStyles: css`
    width: 16px;
    height: 16px;
    opacity: 0.6;
  `,
};

const ExternalLink: FC<{ website?: string; investorRelationsUrl?: string }> = ({ website, investorRelationsUrl }) => {
  const { t } = useTranslation();
  if (!website && !investorRelationsUrl) return null;
  if (investorRelationsUrl)
    return (
      <a href={investorRelationsUrl} css={styles.externalLink}>
        {t('symbol.investorRelations')} <img src={externalLink} css={styles.externalLinkIcon} />
      </a>
    );

  return (
    <a href={website} css={styles.externalLink}>
      {t('symbol.website')}
      <img src={externalLink} css={styles.externalLinkIcon} />
    </a>
  );
};

export const shortenDescription = (description: string): string => {
  // Common abbreviations that shouldn't be treated as sentence endings
  const abbreviations = /\b(?:Inc|Corp|Ltd|Co|LLC|L\.P|Dr|Mr|Mrs|Ms|Jr|Sr|vs|etc|No|Vol|Rev|Est|approx)$/i;

  // Multi-period abbreviations (e.g., i.e., a.m., p.m., U.S., U.K., Ph.D., M.D.)
  // At each period, reconstruct what the abbreviation looks like up to this point:
  // - First period in "e.g.": before="e", after="g." -> reconstructed="e.g" -> matches
  // - Second period in "e.g.": before="e.g", after=" " -> reconstructed="e.g." -> matches
  const multiPeriodPatterns = /\b(?:e\.g|i\.e|a\.m|p\.m|U\.S|U\.K|Ph\.D|M\.D)\.?$/i;
  const isMultiPeriodAbbreviation = (before: string, after: string) => {
    const nextChar = after.match(/^([a-z])/i)?.[1] || '';
    const reconstructed = before + '.' + nextChar;
    return multiPeriodPatterns.test(reconstructed);
  };

  // Find all potential sentence boundaries (. ! ?)
  const boundaries: number[] = [];
  for (let i = 0; i < description.length; i++) {
    const char = description[i];
    if (char === '.' || char === '!' || char === '?') {
      // Check if this is a real sentence boundary
      const before = description.slice(0, i);
      const after = description.slice(i + 1);

      // Skip if preceded by an abbreviation
      if (char === '.' && abbreviations.test(before)) continue;

      // Skip if part of a multi-period abbreviation (e.g., i.e.)
      if (char === '.' && isMultiPeriodAbbreviation(before, after)) continue;

      // Skip if preceded by a single capital letter (e.g., "John S. Smith")
      if (char === '.' && /\b[A-Z]$/.test(before)) continue;

      // Skip if part of a decimal number (digit before and digit after)
      if (char === '.' && /\d$/.test(before) && /^\d/.test(after)) continue;

      // This looks like a real sentence boundary
      boundaries.push(i);
    }
  }

  // Extract first two sentences
  if (boundaries.length >= 2) {
    return description.slice(0, boundaries[1] + 1).trim();
  } else if (boundaries.length === 1) {
    return description.slice(0, boundaries[0] + 1).trim();
  }

  // Fallback: truncate to 50 words
  const shortened = description.split(' ').slice(0, 50).join(' ');
  return shortened.match(/[.,:!?]$/g) ? shortened.slice(0, -1) : shortened;
};

const formatCapitalCase = (str: string) => {
  return str
    .split(/[\s-]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const Info: FC<Props> = ({
  name,
  category,
  description,
  website,
  investorRelationsUrl,
  industry,
  sector,
  ceo,
  employees,
  headquarters,
  isEtfOrMutualFund = false,
}) => {
  const { t } = useTranslation();
  const [isDescriptionShown, setIsDescriptionShown] = useState<boolean>(false);
  const history = useHistory();
  const labelToValueMapping = {
    [t('symbol.industry')]: industry,
    [t('symbol.sector')]: sector,
    [t('symbol.ceo')]: ceo,
    [t('symbol.numberOfEmployees')]: employees?.toLocaleString(),
    [t('symbol.headquarters')]: headquarters,
  };

  const tableContents = Object.entries(labelToValueMapping).filter(([, value]) => !!value);

  if (!description && !website && !investorRelationsUrl && tableContents.length === 0) return null;

  const shortenedDescription = description ? shortenDescription(description) : '';
  // Normalize whitespace for comparison to avoid false "Read more" when content is identical
  const normalize = (s: string) => s.replace(/\s+/g, ' ').trim();
  const hasMoreContent = description ? normalize(shortenedDescription) !== normalize(description) : false;

  const tags = [
    category && { label: formatCapitalCase(category), type: 'Category' },
    sector && { label: formatCapitalCase(sector), type: 'Sector' },
    industry && { label: formatCapitalCase(industry), type: 'Industry' },
  ].filter(Boolean);

  return (
    <div>
      <SectionHeading text={t('symbol.about', { name })} />
      {tags.length > 0 && (
        <div css={styles.attributesContainer}>
          <span css={styles.attributeText}>
            {tags.map(
              (tag, index) =>
                tag && (
                  <span key={tag.type}>
                    {tag.label}
                    {index < tags.length - 1 && ' • '}
                  </span>
                ),
            )}
          </span>
        </div>
      )}
      <ExternalLink website={website} investorRelationsUrl={investorRelationsUrl} />
      {description ? (
        <p css={styles.shortenedDescription}>
          {shortenedDescription}
          {hasMoreContent && (
            <>
              ...{' '}
              <Button
                onClick={() => setIsDescriptionShown(true)}
                css={css`
                  ${fontStyles.mouse}
                `}
              >
                {t('symbol.readMore')}
              </Button>
            </>
          )}
        </p>
      ) : null}
      <Takeover
        isShown={isDescriptionShown}
        onClose={() => setIsDescriptionShown(false)}
        title={t('symbol.about', { name })}
        name="AboutInvestment"
      >
        <p
          css={css`
            ${fontStyles.cat}
          `}
        >
          {description}
        </p>
      </Takeover>
      <div>
        <h2 css={styles.sectionHeadingStyles}>{t('symbol.askBloomAI')}</h2>
        <p css={styles.helperTextStyles}>{t('symbol.tapQuestionToChat')}</p>
        <div css={styles.chatButtonsRowStyles}>
          {isEtfOrMutualFund ? (
            <>
              <button
                css={styles.chatButtonStyles}
                onClick={() =>
                  history.push('/chat', {
                    message: t('symbol.chatPrompt.compareFunds', {
                      name,
                      category:
                        category === 'ETF' ? t('symbol.chatPrompt.categoryEtfs') : t('symbol.chatPrompt.categoryFunds'),
                    }),
                    from: 'symbol',
                  })
                }
              >
                <img src={chatIcon} alt="" css={styles.chatIconStyles} />
                {t('symbol.compareFunds')}
              </button>
              <button
                css={styles.chatButtonStyles}
                onClick={() =>
                  history.push('/chat', {
                    message: t('symbol.chatPrompt.catalystsEtf', { name }),
                    from: 'symbol',
                  })
                }
              >
                <img src={chatIcon} alt="" css={styles.chatIconStyles} />
                {t('symbol.findCatalysts')}
              </button>
              <button
                css={styles.chatButtonStyles}
                onClick={() =>
                  history.push('/chat', {
                    message: t('symbol.chatPrompt.risks', { name }),
                    from: 'symbol',
                  })
                }
              >
                <img src={chatIcon} alt="" css={styles.chatIconStyles} />
                {t('symbol.checkRisks')}
              </button>
            </>
          ) : (
            <>
              <button
                css={styles.chatButtonStyles}
                onClick={() =>
                  history.push('/chat', {
                    message: t('symbol.chatPrompt.explainBusiness', { name }),
                    from: 'symbol',
                  })
                }
              >
                <img src={chatIcon} alt="" css={styles.chatIconStyles} />
                {t('symbol.explainBusiness')}
              </button>
              <button
                css={styles.chatButtonStyles}
                onClick={() =>
                  history.push('/chat', {
                    message: t('symbol.chatPrompt.analyzeMarketPosition', { name }),
                    from: 'symbol',
                  })
                }
              >
                <img src={chatIcon} alt="" css={styles.chatIconStyles} />
                {t('symbol.analyzeMarketPosition')}
              </button>
              <button
                css={styles.chatButtonStyles}
                onClick={() =>
                  history.push('/chat', {
                    message: t('symbol.chatPrompt.catalysts', { name }),
                    from: 'symbol',
                  })
                }
              >
                <img src={chatIcon} alt="" css={styles.chatIconStyles} />
                {t('symbol.findCatalysts')}
              </button>
              <button
                css={styles.chatButtonStyles}
                onClick={() =>
                  history.push('/chat', {
                    message: t('symbol.chatPrompt.risks', { name }),
                    from: 'symbol',
                  })
                }
              >
                <img src={chatIcon} alt="" css={styles.chatIconStyles} />
                {t('symbol.checkRisks')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Info;
